import React from 'react';
import { Box, Typography, Select, MenuItem, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Dialog, FormControl, Divider } from "@mui/material";
import { SelectChangeEvent } from '@mui/material/Select';
import type { FC, ReactElement } from 'react';
import PropTypes from 'prop-types';
import { useFirebase } from "contexts/Firebase";
import { useAuth } from "contexts/User";
import { doc, setDoc } from "firebase/firestore";

// Special IDs for the additional options
const NO_PLAN_ID = 'no-plan';
const UNKNOWN_PLAN_ID = 'unknown-plan';

interface PremiumTier {
    employeeContribution: number;
    employerContribution: number;
    totalPremium: number;
}

interface FourTierPremium {
    employeeOnly: PremiumTier;
    employeePlusChildren: PremiumTier;
    employeePlusSpouse: PremiumTier;
    family: PremiumTier;
}

interface BenefitPlan {
    id: string;
    name: string;
    carrier: string;
    planNetworkType: string;
    individualDeductible: number;
    familyDeductible: number;
    individualOutOfPocketMaximum: number;
    familyOutOfPocketMaximum: number;
    fourTierPremium: FourTierPremium;
}

interface CurrentSettings {
    insuranceStatus: boolean | null;
    effectiveDate: string | null;
    selectedPlan: any;
    whenBenefits: any[];
    benefitPlan: string | null;
}

interface PlanSelectionDialogProps {
    open: boolean;
    onClose: () => void;
    selectedPlan: string;
    onPlanSelect: (planId: string) => void;
    onConfirm: () => void;
    benefitPlans: BenefitPlan[];
    household: any;
    setCurrentSettings: React.Dispatch<React.SetStateAction<CurrentSettings>>;
}

interface PlanDetailsTableProps {
    selectedPlanDetails: BenefitPlan;
    premiumTier: keyof FourTierPremium;
}

const householdValidator = PropTypes.oneOfType([
    PropTypes.shape({}),
    PropTypes.object,
    PropTypes.oneOf([null, undefined])
]);

const premiumTierShape = PropTypes.shape({
    employeeContribution: PropTypes.number.isRequired,
    employerContribution: PropTypes.number.isRequired,
    totalPremium: PropTypes.number.isRequired,
});

const benefitPlanShape = PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    carrier: PropTypes.string.isRequired,
    planNetworkType: PropTypes.string.isRequired,
    individualDeductible: PropTypes.number.isRequired,
    familyDeductible: PropTypes.number.isRequired,
    individualOutOfPocketMaximum: PropTypes.number.isRequired,
    familyOutOfPocketMaximum: PropTypes.number.isRequired,
    fourTierPremium: PropTypes.shape({
        employeeOnly: premiumTierShape.isRequired,
        employeePlusChildren: premiumTierShape.isRequired,
        employeePlusSpouse: premiumTierShape.isRequired,
        family: premiumTierShape.isRequired,
    }).isRequired,
});

// Move these outside the component to prevent recreation
const determinePremiumTier = (members: any): keyof FourTierPremium => {
    if (!members) return 'employeeOnly';
    
    const memberCount = Object.keys(members).length;
    const hasSpouse = Object.values(members).some((member: any) => member.relationship === 'spouse');
    const hasChildren = Object.values(members).some((member: any) => member.relationship === 'child');

    if (memberCount === 1) return 'employeeOnly';
    if (hasSpouse && hasChildren) return 'family';
    if (hasSpouse) return 'employeePlusSpouse';
    if (hasChildren) return 'employeePlusChildren';
    return 'employeeOnly';
};

// Create a memoized table component
const PlanDetailsTable: FC<PlanDetailsTableProps> = React.memo(({ selectedPlanDetails, premiumTier }) => (
    <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper' }}>
                        Detail
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper' }}>
                        Value
                    </TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                <TableRow>
                    <TableCell component="th" scope="row">Monthly Premium</TableCell>
                    <TableCell>${selectedPlanDetails.fourTierPremium[premiumTier].totalPremium}</TableCell>
                </TableRow>
                <TableRow>
                    <TableCell component="th" scope="row">Your Contribution</TableCell>
                    <TableCell>${selectedPlanDetails.fourTierPremium[premiumTier].employeeContribution}</TableCell>
                </TableRow>
                <TableRow>
                    <TableCell component="th" scope="row">Employer Contribution</TableCell>
                    <TableCell>${selectedPlanDetails.fourTierPremium[premiumTier].employerContribution}</TableCell>
                </TableRow>
                <TableRow sx={{ backgroundColor: 'background.paper' }}>
                    <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>
                        Individual Coverage
                    </TableCell>
                </TableRow>
                <TableRow>
                    <TableCell component="th" scope="row">Individual Deductible</TableCell>
                    <TableCell>${selectedPlanDetails.individualDeductible}</TableCell>
                </TableRow>
                <TableRow>
                    <TableCell component="th" scope="row">Individual Out-of-Pocket Maximum</TableCell>
                    <TableCell>${selectedPlanDetails.individualOutOfPocketMaximum}</TableCell>
                </TableRow>
                <TableRow sx={{ backgroundColor: 'background.paper' }}>
                    <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>
                        Family Coverage
                    </TableCell>
                </TableRow>
                <TableRow>
                    <TableCell component="th" scope="row">Family Deductible</TableCell>
                    <TableCell>${selectedPlanDetails.familyDeductible}</TableCell>
                </TableRow>
                <TableRow>
                    <TableCell component="th" scope="row">Family Out-of-Pocket Maximum</TableCell>
                    <TableCell>${selectedPlanDetails.familyOutOfPocketMaximum}</TableCell>
                </TableRow>
            </TableBody>
        </Table>
    </TableContainer>
));

PlanDetailsTable.propTypes = {
    selectedPlanDetails: benefitPlanShape.isRequired,
    premiumTier: PropTypes.oneOf<keyof FourTierPremium>(['employeeOnly', 'employeePlusChildren', 'employeePlusSpouse', 'family']).isRequired,
};

PlanDetailsTable.displayName = 'PlanDetailsTable';

const PlanSelectionDialog: FC<PlanSelectionDialogProps> = React.memo(({
    open,
    onClose,
    selectedPlan,
    onPlanSelect,
    onConfirm,
    benefitPlans,
    household,
    setCurrentSettings
}): ReactElement => {
    const { user } = useAuth();
    const { firestore } = useFirebase();

    // Memoize selected plan details
    const selectedPlanDetails = React.useMemo(() => 
        benefitPlans.find(plan => plan.id === selectedPlan),
        [benefitPlans, selectedPlan]
    );

    // Memoize premium tier calculation
    const premiumTier = React.useMemo(() => 
        determinePremiumTier(household),
        [household]
    );

    // Memoize handler
    const handlePlanChange = React.useCallback(async (event: SelectChangeEvent<string>) => {
        const planId = event.target.value;
        onPlanSelect(planId);

        // Save to Firestore
        if (user?.uid && firestore) {
            // Handle special cases
            if (planId === NO_PLAN_ID) {
                try {
                    await setDoc(doc(firestore, `users/${user.uid}/settings/chat`), {
                        benefitPlan: "No Plan",
                        updatedAt: new Date()
                    }, { merge: true });

                    setCurrentSettings((prev: CurrentSettings) => ({
                        ...prev,
                        benefitPlan: "No Plan"
                    }));
                } catch (error) {
                    console.error("Error saving benefit plan:", error);
                }
                return;
            }
            
            if (planId === UNKNOWN_PLAN_ID) {
                try {
                    await setDoc(doc(firestore, `users/${user.uid}/settings/chat`), {
                        benefitPlan: "Unknown Plan",
                        updatedAt: new Date()
                    }, { merge: true });

                    setCurrentSettings((prev: CurrentSettings) => ({
                        ...prev,
                        benefitPlan: "Unknown Plan"
                    }));
                } catch (error) {
                    console.error("Error saving benefit plan:", error);
                }
                return;
            }

            // Handle normal plan selection
            const selectedPlan = benefitPlans.find(plan => plan.id === planId);
            if (selectedPlan) {
                try {
                    await setDoc(doc(firestore, `users/${user.uid}/settings/chat`), {
                        benefitPlan: selectedPlan.name,
                        updatedAt: new Date()
                    }, { merge: true });

                    // Update current settings immediately with proper typing
                    setCurrentSettings((prev: CurrentSettings) => ({
                        ...prev,
                        benefitPlan: selectedPlan.name
                    }));
                } catch (error) {
                    console.error("Error saving benefit plan:", error);
                }
            }
        }
    }, [onPlanSelect, user?.uid, firestore, benefitPlans, setCurrentSettings]);

    // Memoize the plan options
    const planOptions = React.useMemo(() => {
        const options = benefitPlans.map((plan) => (
            <MenuItem key={plan.id} value={plan.id}>
                {plan.name}
            </MenuItem>
        ));
        
        // Add the additional options
        options.push(
            <MenuItem key={NO_PLAN_ID} value={NO_PLAN_ID}>
                I don&apos;t have a plan
            </MenuItem>
        );
        options.push(
            <MenuItem key={UNKNOWN_PLAN_ID} value={UNKNOWN_PLAN_ID}>
                I don&apos;t know what plan I&apos;m on
            </MenuItem>
        );
        
        return options;
    }, [benefitPlans]);

    // Render insurance details based on selected plan
    const renderPlanDetails = React.useMemo(() => {
        if (!selectedPlanDetails) return null;
        
        return (
            <Box sx={{ mt: 3 }}>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body1">Insurer:</Typography>
                    <Typography variant="body1" fontWeight="bold">{selectedPlanDetails.carrier}</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body1">Plan Type:</Typography>
                    <Typography variant="body1" fontWeight="bold">{selectedPlanDetails.planNetworkType}</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body1">Insurance Type:</Typography>
                    <Typography variant="body1" fontWeight="bold">COBRA</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body1">Monthly Premium:</Typography>
                    <Typography variant="body1" fontWeight="bold">${selectedPlanDetails.fourTierPremium[premiumTier].totalPremium.toFixed(2)}</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body1">Deductible:</Typography>
                    <Typography variant="body1" fontWeight="bold">${selectedPlanDetails.individualDeductible.toFixed(2)}</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body1">Max Out of Pocket:</Typography>
                    <Typography variant="body1" fontWeight="bold">${selectedPlanDetails.individualOutOfPocketMaximum.toFixed(2)}</Typography>
                </Box>
            </Box>
        );
    }, [selectedPlanDetails, premiumTier]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            TransitionProps={{
                mountOnEnter: true,
                unmountOnExit: true
            }}
        >
            <Box sx={{ p: 4 }}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: "bold" }}>
                    Confirm current plan details
                </Typography>
                
                <Box sx={{ mb: 3 }}>
                    <Typography variant="body1" sx={{ mb: 1, fontWeight: "medium" }}>
                        Select your plan:
                    </Typography>
                    <FormControl fullWidth>
                        <Select
                            value={selectedPlan}
                            onChange={handlePlanChange}
                            displayEmpty
                            sx={{ mb: 2 }}
                        >
                            {planOptions}
                        </Select>
                    </FormControl>
                    
                    {renderPlanDetails}
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={!selectedPlan}
                        onClick={onConfirm}
                        sx={{ textTransform: 'none' }}
                    >
                        Confirm and Continue
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
});

PlanSelectionDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    selectedPlan: PropTypes.string.isRequired,
    onPlanSelect: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    benefitPlans: PropTypes.arrayOf(benefitPlanShape).isRequired,
    household: householdValidator,
    setCurrentSettings: PropTypes.func.isRequired
} as any;

PlanSelectionDialog.displayName = 'PlanSelectionDialog';

export default PlanSelectionDialog;